/**
 * Reports → Conversation Reports → Conversation Transfer.
 *
 * One row per hand-off, with the interval bucket the drawer asked for. Same
 * shell as the other reports — toolbar, Table ⇄ Analytics tabs, paging, export
 * — so this file only carries what makes this report itself.
 */
import { useState, type JSX } from 'react'
import { ArrowRightLeft } from 'lucide-react'

import ConversationTransferFilters from '../../../components/reports/conversation-transfer/ConversationTransferFilters.js'
import ConversationTransferAnalytics from '../../../components/reports/conversation-transfer/ConversationTransferAnalytics.js'
import {
  CONVERSATION_TRANSFER_COLUMNS,
  CONVERSATION_TRANSFER_REPORT_ID,
  TRANSFER_DEFAULT_COLUMNS,
  readTransferCell,
  sanitizeTransferColumns,
} from '../../../components/reports/conversation-transfer/columns.js'
import ReportDataTable from '../../../components/reports/shared/ReportDataTable.js'
import ReportShell from '../../../components/reports/shared/ReportShell.js'
import {
  useConversationTransferReport,
  type ConversationTransferFilters as Filters,
} from '../../../hooks/useConversationTransferReport.js'
import { useReportView } from '../../../hooks/useReportView.js'

export default function ConversationTransferReportPage(): JSX.Element {
  const { rows, total, loading, error, hasRun, run, reset } = useConversationTransferReport()
  const [filtersOpen, setFiltersOpen] = useState(true)
  const [ranWith, setRanWith] = useState<Filters | null>(null)

  const { columns, setColumns, setColumnsLocal, commit } = useReportView(
    CONVERSATION_TRANSFER_REPORT_ID,
    TRANSFER_DEFAULT_COLUMNS,
    sanitizeTransferColumns,
  )

  const handleRun = (filters: Filters): void => {
    setRanWith(filters)
    setFiltersOpen(false)
    void run(filters)
  }

  const toggleColumn = (key: string): void => {
    if (CONVERSATION_TRANSFER_COLUMNS.find((c) => c.key === key)?.locked) return
    const next = columns.includes(key)
      ? columns.filter((k) => k !== key)
      : [...columns, key].sort(
          (a, b) => TRANSFER_DEFAULT_COLUMNS.indexOf(a) - TRANSFER_DEFAULT_COLUMNS.indexOf(b),
        )
    setColumns(next)
  }

  const reorder = (from: string, to: string): void => {
    if (from === to) return
    setColumnsLocal((prev) => {
      const next = [...prev]
      const fromIdx = next.indexOf(from)
      const toIdx = next.indexOf(to)
      if (fromIdx === -1 || toIdx === -1) return prev
      next.splice(toIdx, 0, ...next.splice(fromIdx, 1))
      return next
    })
  }

  const subtitle = ranWith
    ? `${total} transfer${total === 1 ? '' : 's'}`
      + ` · ${ranWith.startDate} ${ranWith.startTime} → ${ranWith.endDate} ${ranWith.endTime}`
      + ` · ${ranWith.timezone}`
    : 'Every conversation hand-off in a window — who moved it, who received it, and why'

  return (
    <ReportShell<Record<string, unknown>>
      Icon={ArrowRightLeft}
      title="Conversation Transfer Report"
      subtitle={subtitle}
      hasRun={hasRun}
      ranWith={ranWith}
      loading={loading}
      error={error}
      rows={rows}
      catalog={CONVERSATION_TRANSFER_COLUMNS}
      visibleColumns={columns}
      onToggleColumn={toggleColumn}
      onReorderColumn={reorder}
      onCommitColumns={commit}
      searchPlaceholder="Search transfers…"
      readCell={readTransferCell}
      reportId={CONVERSATION_TRANSFER_REPORT_ID}
      reportName="Conversation Transfer Report"
      exportParams={ranWith && {
        timezone: ranWith.timezone,
        interval: ranWith.interval,
        startDate: ranWith.startDate,
        endDate: ranWith.endDate,
        startTime: ranWith.startTime,
        endTime: ranWith.endTime,
        locationIds: ranWith.locationIds.join(','),
        branchIds: ranWith.branchIds.join(','),
        departmentIds: ranWith.departmentIds.join(','),
      }}
      onOpenFilters={() => setFiltersOpen(true)}
      renderTable={(pageRows, visibleColumns) => (
        <ReportDataTable
          rows={pageRows}
          columns={CONVERSATION_TRANSFER_COLUMNS}
          visibleColumns={visibleColumns}
        />
      )}
      renderAnalytics={(visible) => <ConversationTransferAnalytics rows={visible} />}
      emptyTitle="No report yet"
      emptyDescription="Choose a start date, an end date and an interval in Filters, then run the report."
      noResultsTitle="No transfers in this window"
      noResultsDescription="No conversation was handed off in the selected scope. Try widening the window, or clearing the location filters."
    >
      <ConversationTransferFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onRun={handleRun}
        onReset={() => { setRanWith(null); reset() }}
        running={loading}
      />
    </ReportShell>
  )
}
